import type { QuestionOption } from '@logistics/contracts';

/**
 * Each person's last "SEARCH IN" choice, remembered per browser and per user.
 * Storage can be missing or throw (private windows, blocked site data), so
 * every access is guarded and the choice falls back to every available source.
 */
export type ScopeStorage = Pick<Storage, 'getItem' | 'setItem'>;

const key = (userId: string): string => `fleet.chat.scope.${userId}`;

export function readScope(
  storage: ScopeStorage | undefined,
  userId: string,
  available: readonly QuestionOption[],
): QuestionOption[] {
  try {
    const raw = storage?.getItem(key(userId));
    if (raw === null || raw === undefined) return [...available];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...available];
    const kept = available.filter((option) => parsed.includes(option));
    return kept.length > 0 ? kept : [...available];
  } catch {
    return [...available];
  }
}

export function writeScope(
  storage: ScopeStorage | undefined,
  userId: string,
  scope: readonly QuestionOption[],
): void {
  try {
    storage?.setItem(key(userId), JSON.stringify(scope));
  } catch {
    // Not remembered this time; the chips still switch.
  }
}

/** Ticks or unticks one source; the last one ticked cannot be unticked. */
export function toggleScope(
  scope: readonly QuestionOption[],
  option: QuestionOption,
): QuestionOption[] {
  if (!scope.includes(option)) return [...scope, option];
  return scope.length === 1 ? [...scope] : scope.filter((o) => o !== option);
}

/** The browser's localStorage, or nothing when merely touching it throws. */
export function browserStorage(): ScopeStorage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
